import React from 'react';
import type { FromTo } from '../core/speed-optimization';
import {
  formatCurrency,
  getNumberFormatter2,
  pluralize,
} from '../../lib/format';
import { useDefaultCurrency } from '../../state/zustand';
import { equalToPrecision } from '@seaber/common/lib/math';
import type { TceResult } from '../shipment/TCE';

export interface LegChange {
  /** Origin location name */
  origin: string;
  /** Destination location name */
  destination: string;
  /** Speed in knots */
  speed: FromTo;
  /** Duration in hours */
  duration: FromTo;
}

export interface SpeedOptimizationToastProps {
  legChanges: LegChange[];
  tce: FromTo<NonNullable<TceResult>>;
}

const formatValue = getNumberFormatter2(0, 1);

export const SpeedOptimizationToast: React.FC<SpeedOptimizationToastProps> = ({
  legChanges,
  tce,
}) => {
  const originalVoyageCost = tce.from.voyageCost;
  const newVoyageCost = tce.to.voyageCost;

  const originalTce = tce.from.tce;
  const newTce = tce.to.tce;

  const baseCurrency = useDefaultCurrency();

  const significantChanges = legChanges.filter((change) => {
    return (
      !equalToPrecision(change.speed.from, change.speed.to, 1) ||
      !equalToPrecision(change.duration.from, change.duration.to, 1)
    );
  });

  const costChange = newVoyageCost - originalVoyageCost;
  const tceImprovement = newTce - originalTce;

  return (
    <div style={{ fontFamily: 'inherit' }}>
      <div style={{ fontWeight: 600, marginBottom: '16px' }}>
        {pluralize`Speed optimization completed for ${legChanges.length} leg`}
      </div>

      {significantChanges.length === 0 && (
        <div style={{ fontSize: '14px', opacity: 0.7, marginBottom: '8px' }}>
          No changes to leg speeds
        </div>
      )}

      {significantChanges.length > 0 && (
        <>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                Vessel TCE Δ:{' '}
                {formatCurrency(tceImprovement, 0, baseCurrency, {
                  signAlways: true,
                })}
                /day
              </div>
              <div style={{ fontSize: '13px', opacity: 0.7, fontWeight: 500 }}>
                {formatCurrency(originalTce, 0, baseCurrency)}/day
                <span style={{ margin: '0 8px', opacity: 0.5 }}>→</span>
                {formatCurrency(newTce, 0, baseCurrency)}/day
              </div>
            </div>

            <div>
              <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                Vessel Cost Δ:{' '}
                {formatCurrency(costChange, 0, baseCurrency, {
                  signAlways: true,
                })}
              </div>
              <div style={{ fontSize: '13px', opacity: 0.7, fontWeight: 500 }}>
                {formatCurrency(originalVoyageCost, 0, baseCurrency)}
                <span style={{ margin: '0 8px', opacity: 0.5 }}>→</span>
                {formatCurrency(newVoyageCost, 0, baseCurrency)}
              </div>
            </div>
          </div>

          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '13px',
            }}
          >
            <tbody>
              {legChanges.map((change, index) => {
                const speedChanged = !equalToPrecision(
                  change.speed.from,
                  change.speed.to,
                  1,
                );
                const durationChanged = !equalToPrecision(
                  change.duration.from,
                  change.duration.to,
                  1,
                );

                return (
                  <tr
                    key={index}
                    style={{
                      borderTop:
                        index === 0 ? '1px solid rgba(0, 0, 0, 0.08)' : 'none',
                      borderBottom:
                        index < legChanges.length - 1
                          ? '1px solid rgba(0, 0, 0, 0.08)'
                          : 'none',
                    }}
                  >
                    <td
                      style={{
                        padding: '10px 16px 10px 0',
                        fontWeight: speedChanged || durationChanged ? 600 : 500,
                        opacity: speedChanged || durationChanged ? 0.8 : 0.5,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '200px',
                      }}
                    >
                      {change.origin} → {change.destination}
                    </td>
                    <td
                      style={{
                        padding: '10px 16px',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        verticalAlign: 'middle',
                      }}
                    >
                      <div style={{ opacity: speedChanged ? 0.8 : 0.5 }}>
                        {formatValue.format(change.speed.from)} kn
                      </div>
                      {speedChanged && (
                        <>
                          <div
                            style={{
                              opacity: 0.4,
                              fontSize: '11px',
                              margin: '2px 0',
                            }}
                          >
                            ↓
                          </div>
                          <div style={{ opacity: 0.8 }}>
                            {formatValue.format(change.speed.to)} kn
                          </div>
                        </>
                      )}
                    </td>
                    <td
                      style={{
                        padding: '10px 16px',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        verticalAlign: 'middle',
                      }}
                    >
                      <div style={{ opacity: durationChanged ? 0.8 : 0.5 }}>
                        {formatValue.format(change.duration.from)} h
                      </div>
                      {durationChanged && (
                        <>
                          <div
                            style={{
                              opacity: 0.4,
                              fontSize: '11px',
                              margin: '2px 0',
                            }}
                          >
                            ↓
                          </div>
                          <div style={{ opacity: 0.8 }}>
                            {formatValue.format(change.duration.to)} h
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};
